import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  Input,
  Button,
} from "@chakra-ui/react";
import { FILE_MANAGEMENT, FILE_TYPE } from "@root/constants";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import UploadProgressList from "./UploadProgressList";
import FileDropZone from "./FileDropZone";
import { FileUploadProgress, UploadStatus, UploadStatusType } from "./types";

interface Project {
  id: number;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  folderLoading: boolean;
  progress: number;
  createType: FILE_MANAGEMENT.FOLDER | FILE_MANAGEMENT.FILE | "project";
  handleCreateClick: (
    type: FILE_MANAGEMENT.FOLDER | FILE_MANAGEMENT.FILE | "project",
    payload: any
  ) => void;
  selectedFolder: { id: string; name: string } | null;
  selectedProject: Project | null;
  setProgress: React.Dispatch<React.SetStateAction<number>>;
  getFolders: (payload: { projectId: number | undefined }) => void;
  folders: any;
  fileUploadLoading: boolean;
  projectLoading?: boolean;
  getFiles: (payload: { folderId: string | undefined }) => void;
  setFilePage: (num: number) => void;
  setFolderPage: (num: number) => void;
}

const CreateModal: React.FC<Props> = ({
  isOpen,
  onClose,
  folderLoading,
  progress,
  createType,
  handleCreateClick,
  selectedFolder,
  selectedProject,
  setProgress,
  getFolders,
  fileUploadLoading,
  projectLoading = false,
  getFiles,
  setFilePage,
  setFolderPage,
}) => {
  const { t } = useTranslation("common");
  const [folderName, setFolderName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>(
    []
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  /** -------------------- Folder Creation Progress -------------------- */
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (folderLoading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress((prev: number) => (prev < 95 ? prev + 5 : prev));
      }, 300);
    } else {
      setProgress(100);
      setTimeout(() => setProgress(0), 1000);
    }

    return () => clearInterval(interval);
  }, [folderLoading, setProgress]);

  /** -------------------- File Selection Handlers -------------------- */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setSelectedFiles(files);
  };

  const handleBoxClick = () => fileInputRef.current?.click();

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    setSelectedFiles(files);
  };

  /** -------------------- Upload Utility Functions -------------------- */
  const checkIfAllCompleted = () => {
    setUploadProgress((prev) => {
      const allDone = prev.every(
        (item) =>
          item.status === UploadStatus.COMPLETED ||
          item.status === UploadStatus.CANCELLED ||
          item.status === UploadStatus.ERROR
      );
      if (allDone) {
        setTimeout(() => {
          setIsUploading(false);
          setUploadProgress([]);
          setSelectedFiles(null);
          setFilePage(1);

          getFiles({ folderId: selectedFolder?.id });
          // Close the modal after upload completion
          onClose();
        }, 2000);
      }
      return prev;
    });
  };

  const uploadFileWithProgress = async (
    file: File,
    index: number,
    abortController: AbortController
  ) => {
    const formData = new FormData();
    formData.append("folderId", selectedFolder?.id?.toString() ?? "");
    formData.append("projectId", selectedProject?.id?.toString() ?? "");
    formData.append("file", file);
    formData.append("name", file.name);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Upload progress tracking
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress((prev) =>
            prev.map((item, i) =>
              i === index ? { ...item, progress: percent } : item
            )
          );
        }
      });

      // Success handler
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress((prev) =>
            prev.map((item, i) =>
              i === index
                ? { ...item, progress: 100, status: UploadStatus.COMPLETED }
                : item
            )
          );
          checkIfAllCompleted();
          resolve(xhr.response);
        } else {
          const errorMessage = t("projects.uploadFailed");
          setUploadProgress((prev) =>
            prev.map((item, i) =>
              i === index
                ? { ...item, status: UploadStatus.ERROR, errorMessage }
                : item
            )
          );
          checkIfAllCompleted();
          reject(new Error(errorMessage));
        }
      });

      // Error handler
      xhr.addEventListener("error", () => {
        const errorMessage = t("projects.networkError");
        setUploadProgress((prev) =>
          prev.map((item, i) =>
            i === index
              ? { ...item, status: UploadStatus.ERROR, errorMessage }
              : item
          )
        );
        checkIfAllCompleted();
        reject(new Error(errorMessage));
      });

      // Abort handler
      xhr.addEventListener("abort", () => {
        setUploadProgress((prev) =>
          prev.map((item, i) =>
            i === index ? { ...item, status: UploadStatus.CANCELLED } : item
          )
        );
        checkIfAllCompleted();
        reject(new Error(t("projects.uploadCancelled")));
      });

      abortController.signal.addEventListener("abort", () => xhr.abort());

      const baseURL = process.env.REACT_APP_BASE_URL ?? "";
      const apiEndpoint = `${baseURL}/api/admin/documents`;

      xhr.open("POST", apiEndpoint);
      const token = localStorage.getItem("authToken");
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.send(formData);
    });
  };

  const handleCancelUpload = (index: number) => {
    const fileItem = uploadProgress[index];
    if (
      fileItem?.abortController &&
      fileItem.status === UploadStatus.UPLOADING
    ) {
      fileItem.abortController.abort();
    }
  };

  /** -------------------- Project/Folder/File Creation -------------------- */
  const handleCreate = async () => {
    if (!createType) return;

    if (createType === "project") {
      const payload = {
        name: projectName,
        description: "",
      };
      try {
        await handleCreateClick(createType, payload);
        // Reset form after successful creation
        setProjectName("");
      } catch (error) {
        // Error handled in handleCreateClick
      }
    } else if (createType === FILE_MANAGEMENT.FOLDER) {
      const payload = {
        name: folderName,
        description: "",
        projectId: selectedProject?.id,
      };
      try {
        await handleCreateClick(createType, payload);
        // Reset form after successful creation
        setFolderName("");
        setFolderPage(1);
      } catch (error) {
        // Error handled in handleCreateClick
      }
    } else if (createType === FILE_MANAGEMENT.FILE) {
      if (!selectedFiles?.length) return;

      setIsUploading(true);
      const initial = selectedFiles.map((file) => {
        const controller = new AbortController();
        return {
          file,
          progress: 0,
          status: UploadStatus.UPLOADING,
          abortController: controller,
        };
      });
      setUploadProgress(initial);

      selectedFiles.forEach(async (file, index) => {
        try {
          await uploadFileWithProgress(
            file,
            index,
            initial[index].abortController!
          );
        } catch (err) {
          console.error(`Upload failed: ${file.name}`, err);
        }
      });
    }
  };

  /** -------------------- JSX Return -------------------- */
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay bg="blackAlpha.400" zIndex="1400" />
      <ModalContent
        position="fixed"
        bottom="0"
        right="0"
        borderRadius="16px 16px 0 0"
        bg="white"
        zIndex="1400"
        m="0"
      >
        <ModalHeader pb={2}>
          {createType === FILE_MANAGEMENT.FILE && isUploading
            ? `${t("projects.uploading")} - ${
                uploadProgress.filter(
                  (f) => f.status === UploadStatus.COMPLETED
                ).length
              }/${uploadProgress.length} ${
                uploadProgress.length === 1
                  ? t("projects.file")
                  : t("projects.filesLabel")
              }`
            : createType === FILE_MANAGEMENT.FILE
            ? t("projects.uploadFile")
            : createType === "project"
            ? t("projects.createProject")
            : t("projects.createFolder")}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {createType === "project" && (
            <>
              <Text fontSize="sm" mb={1}>
                {t("projects.projectName")}
              </Text>
              <Input
                placeholder={t("projects.enterProjectName")}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                mb={4}
              />
              <Button
                width="full"
                onClick={handleCreate}
                bg="#0E0E0E"
                isDisabled={!projectName.trim() || projectLoading}
                isLoading={projectLoading}
              >
                {t("projects.createProject")}
              </Button>
            </>
          )}

          {createType === FILE_MANAGEMENT.FOLDER && (
            <>
              <Text fontSize="sm" mb={1}>
                {t("projects.folderName")}
              </Text>
              <Input
                placeholder={t("projects.enterFolderName")}
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                mb={4}
              />
              <Button
                width="full"
                onClick={handleCreate}
                bg="#0E0E0E"
                isDisabled={!folderName.trim() || folderLoading}
                isLoading={folderLoading}
              >
                {t("projects.createFolder")}
              </Button>
            </>
          )}

          {createType === FILE_MANAGEMENT.FILE && (
            <Box>
              {isUploading ? (
                <UploadProgressList
                  uploadProgress={uploadProgress}
                  onCancelUpload={handleCancelUpload}
                />
              ) : (
                <FileDropZone
                  isDragging={isDragging}
                  selectedFolder={selectedFolder}
                  selectedFiles={selectedFiles}
                  fileInputRef={fileInputRef}
                  onBoxClick={handleBoxClick}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onFileChange={handleFileChange}
                />
              )}
              <Button
                bg="#0E0E0E"
                width="full"
                onClick={handleCreate}
                isDisabled={!selectedFiles?.length || isUploading}
              >
                {t("projects.uploadFiles")}
              </Button>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateModal;
